import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { AuthShell } from '../components/AuthShell'
import { confirmAccountReactivation } from '../lifecycle/lifecycle.api'
import { LIFECYCLE_CODES, RECOVER_SCREEN_STATES, SUPPORT_MAILTO } from '../lifecycle/lifecycle.constants'
import { queryToken, recoverConfirmState } from '../lifecycle/lifecycle.utils'
import '../../login/login.css'
import '../account.css'

export default function RecoverAccountPage() {
  const { formatMessage: trans } = useIntl()
  const history = useHistory()
  const location = useLocation()
  const token = queryToken(location.search)
  const [state, setState] = useState(token ? RECOVER_SCREEN_STATES.VALIDATING : RECOVER_SCREEN_STATES.MISSING)
  const [apiMessage, setApiMessage] = useState('')

  useEffect(() => {
    let cancelled = false
    if (!token) return undefined
    confirmAccountReactivation(token).then((parsed) => {
      if (cancelled) return
      setApiMessage(parsed.message || '')
      setState(recoverConfirmState(parsed))
    })
    return () => {
      cancelled = true
    }
  }, [token])

  useEffect(() => {
    if (state !== RECOVER_SCREEN_STATES.SUCCESS) return undefined
    const timer = setTimeout(() => history.replace('/'), 2500)
    return () => clearTimeout(timer)
  }, [state, history])

  const copy = {
    [RECOVER_SCREEN_STATES.VALIDATING]: {
      title: trans({ id: 'lifecycle_recover_validating_title', defaultMessage: 'Validating recovery link' }),
      body: trans({ id: 'lifecycle_recover_validating_body', defaultMessage: 'Please wait while we confirm this link.' }),
    },
    [RECOVER_SCREEN_STATES.SUCCESS]: {
      title: trans({ id: 'lifecycle_recover_success_title', defaultMessage: 'Account recovered' }),
      body: trans({
        id: 'lifecycle_recover_success_body',
        defaultMessage: 'You can sign in now. Reactivation does not start a new trial, enable employees, or open Stripe.',
      }),
    },
    [RECOVER_SCREEN_STATES.EXPIRED]: {
      title: trans({ id: 'lifecycle_recover_expired_title', defaultMessage: 'This recovery link is invalid or expired' }),
      body: trans({ id: 'lifecycle_recover_expired_body', defaultMessage: 'Request a new recovery link from the sign-up or login screen.' }),
    },
    [RECOVER_SCREEN_STATES.SUSPENDED]: {
      title: trans({ id: 'lifecycle_account_suspended_title', defaultMessage: 'This account is suspended' }),
      body: trans({ id: 'lifecycle_recover_suspended_body', defaultMessage: 'Suspended accounts cannot be reactivated from this link. Contact support.' }),
    },
    [RECOVER_SCREEN_STATES.TEMPORARY]: {
      title: trans({ id: 'lifecycle_recover_temporary_title', defaultMessage: 'Temporary error' }),
      body: trans({ id: 'lifecycle_recover_temporary_body', defaultMessage: 'We could not complete recovery right now. Try again in a few minutes.' }),
    },
    [RECOVER_SCREEN_STATES.MISSING]: {
      title: trans({ id: 'lifecycle_recover_expired_title', defaultMessage: 'This recovery link is invalid or expired' }),
      body: trans({ id: 'lifecycle_recover_missing_body', defaultMessage: 'This page needs a valid token. Open the link from your email.' }),
    },
  }[state]

  return (
    <AuthShell title={copy.title}>
      <div className="lifecycle-public-panel" data-recover-state={state} data-lifecycle-code={state === RECOVER_SCREEN_STATES.SUSPENDED ? LIFECYCLE_CODES.ACCOUNT_SUSPENDED : undefined}>
        {state === RECOVER_SCREEN_STATES.VALIDATING ? (
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">{trans({ id: 'loading', defaultMessage: 'Loading…' })}</span>
          </div>
        ) : null}
        <p className="lifecycle-public-panel__body">{copy.body}</p>
        {apiMessage ? <p className="lifecycle-notice__api">{apiMessage}</p> : null}
        {state === RECOVER_SCREEN_STATES.SUCCESS ? (
          <button type="button" className="primary-button" onClick={() => history.replace('/')}>
            {trans({ id: 'lifecycle_login', defaultMessage: 'Login' })}
          </button>
        ) : null}
        {state === RECOVER_SCREEN_STATES.SUSPENDED ? (
          <a className="primary-button lifecycle-notice__support" href={SUPPORT_MAILTO}>
            {trans({ id: 'lifecycle_contact_support', defaultMessage: 'Contact support' })}
          </a>
        ) : null}
        {state === RECOVER_SCREEN_STATES.EXPIRED || state === RECOVER_SCREEN_STATES.MISSING || state === RECOVER_SCREEN_STATES.TEMPORARY ? (
          <button type="button" className="secondary-button" onClick={() => history.push('/')}>
            {trans({ id: 'lifecycle_login', defaultMessage: 'Login' })}
          </button>
        ) : null}
      </div>
    </AuthShell>
  )
}
